/**
 * The providers, outermost first, and the router under them.
 *
 * Two of these are React Aria's, and they are the only place the app imports RAC directly:
 *
 * - **`I18nProvider`** carries the Studio's EN/TR choice down to every RAC component, so its
 *   built-in strings ("Remove", the drag-and-drop announcements, "Show suggestions") come out
 *   in the same language as ours, and its collators and number formats follow the locale.
 * - **`RouterProvider`** makes a RAC `Link` navigate through React Router instead of doing a
 *   full page load, while the element stays a real `<a href>` — so middle-click, copy-link and
 *   open-in-new-tab all keep working. Every in-app link has to be a RAC `Link` for that to
 *   mean anything: a bare `<a href>` navigates the document and rebuilds the whole Studio,
 *   which is what `i-0004` was.
 *
 * The two RAC providers live in `AriaProviders.tsx`, so the test harness can mount the same
 * tree this does.
 */

import type { JSX } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router';

import { AriaProviders } from './AriaProviders.js';
import { TooNarrow } from './TooNarrow.js';
import { AuthProvider } from './auth/AuthProvider.js';
import { I18nProvider } from './i18n/I18nProvider.js';
import { routes } from './routes/routes.js';
import { ThemeProvider } from './shell/ThemeProvider.js';
import { ToastProvider } from './shell/ToastProvider.js';

const router = createBrowserRouter(routes);

export function App(): JSX.Element {
  return (
    <ThemeProvider>
      <I18nProvider>
        {/* A phone gets one of these two, never both, and the sheet is what chooses —
            `.app-frame` is `display: contents` above the floor and `display: none` under
            it, so nothing here has to measure anything (`i-0007`). */}
        <div className="app-frame">
          {/* `navigate` goes through the router object rather than `useNavigate`: this sits
              above `RouterProvider` and there is no router context to read yet. */}
          <AriaProviders navigate={router.navigate}>
            <AuthProvider>
              <ToastProvider>
                <RouterProvider router={router} />
              </ToastProvider>
            </AuthProvider>
          </AriaProviders>
        </div>
        <TooNarrow />
      </I18nProvider>
    </ThemeProvider>
  );
}
